<template>
    <gcmcn-layout
        title="CONSUMPTION"
        custom-class="consumption-view"
    >

        <el-row :gutter="20" class="main-content">
            <el-col
                :span="24"
                :style="{height: `calc(100vh - ( ${$store.getters.pageHeight}px))`}"
                style="overflow: auto; padding-bottom: 40px"
            >

                <!-- title to remove from props if we use the version 1 of nsPaginate -->
                <!-- <h2>Consumption by feeds</h2> -->
                <ns-paginate
                    ref="nsPaginate"
                    baseUrl="consumption-by-feed/"
                    tableTitle="Usage per feed"
                    :hide-filters-by-default="true"
                    :listFieldToDisplay="listFieldToDisplay"
                    :extra-hidden-keys="extraHiddenKeys"
                    :custom-filters-to-apply="customFiltersToApply"
                    dark
                >
                    <el-table slot="list" slot-scope="props" class="ns-paginate-table dark-theme" :data="props.list">
                        <el-table-column label="Entity">
                            <template slot-scope="scope">
                                <el-button
                                    type="text"
                                    @click="openNsPaginateModal(scope.row)"
                                >
                                    {{ entitiesByUid(scope.row.scopes.entity_uid).display_name }}
                                </el-button>
                            </template>
                        </el-table-column>
                        <el-table-column label="Feed">
                            <template slot-scope="scope">
                                {{ scope.row.feed.display_name }}
                            </template>
                        </el-table-column>
                        <el-table-column label="Taker">
                            <template slot-scope="scope">
                                {{ takerFormatter(scope.row.taker) }}
                            </template>
                        </el-table-column>
                        <el-table-column label="Duration (sec)" prop="duration_seconds"></el-table-column>
                        <el-table-column label="Start">
                            <template slot-scope="scope">
                                {{ timeFormatter(scope.row.start_datetime) }}
                            </template>
                        </el-table-column>
                        <el-table-column label="End">
                            <template slot-scope="scope">
                                {{ timeFormatter(scope.row.end_datetime) }}
                            </template>
                        </el-table-column>
                        <el-table-column label="Average Bitrate">
                            <template slot-scope="scope">
                                {{ intFormatter(scope.row.average_bitrate) }}
                            </template>
                        </el-table-column>
                    </el-table>
                </ns-paginate>
                <h2 class="section-title">Usage per day</h2>
                <div class="taker-selector-container">
                    <div class="selector-label">Select a taker</div>
                    <div class="gcmcn-form-input">
                        <el-select class="taker-selector" v-model="selectedTaker" placeholder="select a taker" popper-class="taker-dropdown-select">
                            <el-option-group
                                v-for="group in takersByEntity"
                                :key="group.label"
                                :label="group.label"
                            >
                                <el-option
                                    v-for="item in group.options"
                                    :key="item.value"
                                    :label="item.label"
                                    :value="item.value">
                                </el-option>
                            </el-option-group>
                        </el-select>
                    </div>
                </div>
                <div class="date-picker-container">
                    <div class="date-picker-label">Select a date</div>
                    <div class="actions">
                        <date-picker-range ref="date-range" @on-range-selected="setDateRange"></date-picker-range>
                        <el-button type="primary" class="gcmcn-primary-btn small" @click="resetDateRange" style="margin-left: 10px;">Reset</el-button>
                    </div>
                </div>
                <div class="graphs-container" v-if="selectedTaker !== ''" :key="selectedTaker">
                    <ns-loader
                        ref="graph-loader"
                        :toLoad="toLoad"
                        @dataFetched="onDataFetched"
                    >
                        <div slot="content">
                            <div v-if="consInDateRange.length > 0">
                                <graph
                                    title="Feed counts"
                                    :max="maxValue(feedCounts)"
                                    :labels="labels"
                                    :values="feedCounts"
                                    :names="names.feedCounts"
                                    :colors="['#00A3FF']"
                                ></graph>
                                <graph
                                    title="Cumulative durations (sec)"
                                    :max="maxValue(cumulativeDurations)"
                                    :labels="labels"
                                    :values="cumulativeDurations"
                                    :names="names.cumulativeDurations"
                                    :colors="['#00A3FF']"
                                ></graph>
                                <graph
                                    title="Average and max bitrates (bps)"
                                    :max="Math.max(maxValue(averageBitrates), maxValue(maxBitrates))"
                                    :labels="labels"
                                    :values="[averageBitrates, maxBitrates]"
                                    :names="names.bitrates"
                                    :colors="['#00A3FF', '#28E37C']"
                                ></graph>
                            </div>
                            <div v-else class="warning-message">No data available</div>
                        </div>
                    </ns-loader>
                </div>
                <div v-else class="info-message">No data</div>
            </el-col>
        </el-row>
    </gcmcn-layout>
</template>

<script>

import GcmcnLayout from '../../partials/gcmcn-layout.vue';
import NsLoader from 'fundation-app-vuejs/src/components/partials/ns-vue-loader';
import NsPaginate from 'fundation-app-vuejs/src/components/partials/nsPaginate2';
import moment from 'moment';
import schwartz from 'schwartz';
import {mapGetters} from 'vuex';
import Graph from './graph';
import {
    DatePickerRange
} from 'gcmcn-common';
export default {

    components: {
        GcmcnLayout,
        NsLoader,
        DatePickerRange,
        Graph,
        NsPaginate
    },

    props: {

    },

    data (){
        return {
            selectedTaker: '',
            consumptions: [],
            dateRange: [],
            names: {
                feedCounts: ['feeds'],
                cumulativeDurations: ['durations'],
                bitrates: ['average', 'maximum'],
            },
            extraHiddenKeys: ['feed', 'configuration', 'taker', 'scopes'],
            customFiltersToApply: () => {
                return { c_resp_nested: true };
            },
        };
    },

    computed: {
        ...mapGetters(['takers', 'entities', 'entitiesByUid', 'windowWidth']),
        listFieldToDisplay() {
            return [
                {
                    key: 'entity_name',
                    verbose: 'Entity',
                },
                {
                    key: 'feed_name',
                    verbose: 'Feed',
                },
                {
                    key: 'taker',
                    verbose: 'Related taker',
                    valueFormatter: this.takerFormatter
                },
                {
                    key: 'duration_seconds',
                    verbose: 'Duration (sec)',
                },
                {
                    key: 'start_datetime',
                    verbose: 'Start',
                    valueFormatter: this.timeFormatter
                },
                {
                    key: 'end_datetime',
                    verbose: 'End',
                    valueFormatter: this.timeFormatter
                },
                {
                    key: 'average_bitrate',
                    verbose: 'Average Bitrate',
                    valueFormatter: this.intFormatter
                },
            ];
        },
        consInDateRange() {
            let start;
            let end;
            if (this.dateRange.length > 0) {
                start = moment(this.dateRange[0]);
                end = moment(this.dateRange[1]);
            } else {
                start = moment().subtract(30, 'days');
                end = moment();
            }
            let cons = this.consumptions.filter(cons => {
                let date = moment(cons.date);
                return date.isBetween(start, end) || date.isSame(start) || date.isSame(end);
            });
            return cons;
        },
        labels() {
            let dates = this.consInDateRange.map(cons => cons.date);
            dates.sort();
            dates = dates.map(date => moment(date).format('Do MMM'));
            return dates;
        },
        feedCounts() {
            return schwartz(this.consInDateRange, cons => cons.date).map(cons => cons.feed_count);
        },
        cumulativeDurations() {
            return schwartz(this.consInDateRange, cons => cons.date).map(cons => cons.cumulative_duration_seconds);
        },
        averageBitrates() {
            return schwartz(this.consInDateRange, cons => cons.date).map(cons => cons.average_bitrate);
        },
        maxBitrates() {
            return schwartz(this.consInDateRange, cons => cons.date).map(cons => cons.max_bitrate);
        },
        toLoad() {
            return [
                {
                    name: 'consumptions',
                    model: 'consumptionByDays',
                    filters: {
                        taker_uid: this.selectedTaker
                    }
                }
            ];
        },
        takersByEntity() {
            let options = [];
            this.entities.forEach(entity => {
                let subOptions = [];
                this.takers.filter(taker => {
                    return taker.scopes.entity_uid === entity.uid;
                }).forEach(taker => {
                    subOptions.push({
                        label: taker.name,
                        value: taker.uid
                    });
                });
                if (subOptions.length) {
                    options.push({
                        label: entity.display_name,
                        options: subOptions
                    });
                }
            });
            return options;
        }
    },

    mounted (){

    },

    methods: {
        openNsPaginateModal (object){
            this.$refs.nsPaginate.showModalEditObject(object);
        },
        takerFormatter(taker) {
            return taker.name;
        },
        timeFormatter(time) {
            return moment.utc(time).format('YYYY-MM-DD HH:mm:ss [UTC]');
        },
        intFormatter(number) {
            return parseInt(number);
        },
        resetDateRange() {
            this.$refs['date-range'].clear();
        },
        maxValue(array) {
            if (array.length === 0) return 0;
            let max = Math.max(...array);
            return max + Math.max(1, parseInt(max/10));
        },
        onDataFetched(res) {
            this.consumptions = res.consumptions;
        },
        setDateRange(payload) {
            this.dateRange = payload;
        }
    },
};
</script>

<style lang="scss">
.modal-edit{
    background: linear-gradient(180deg, #111 12.5%, #222 100%) !important;
    border: none;
    .el-message-box__content, .el-message-box__title{
        color: #bbb;
    }
    hr{
        border-color: #bbb;
    }
    .json-view-item {
        .data-key, .value-key *{
            color: #bbb !important;
        }
    }
}
.consumption-view {
    .section-title {
        margin: 0;
        padding: 30px 30px 0;
        font-weight: 300;
    }
    .taker-selector {
        .el-input__inner::placeholder {
            opacity: 1;
            color: #111111 !important;
        }
    }
    .warning-message, .info-message {
        margin-top: 40px;
        font-size: 20px;
    }
    .info-message {
        padding: 0 30px;
    }
    .graphs-container {
        padding: 0 30px;
    }
    .taker-selector-container {
        margin-top: 20px;
        width: 400px;
        padding: 0 30px;
        .selector-label {
            font-size: 16px;
            margin-bottom: 10px;
        }
    }
}
.date-picker-container {
    padding: 0 30px;
    .actions {
        display: flex;
    }
    .date-picker-label {
        font-size: 16px;
        margin-bottom: 10px;
    }
    margin-bottom: 20px;
}
.taker-dropdown-select {
    background-color: #444444;
    border-color: #444444;
    .el-select-group__title {
        color: #BBBBBB !important;
    }
    .el-select-dropdown__item {
        color: #EEEEEE !important;
    }
    .el-select-dropdown__item.hover, .el-select-dropdown__item:hover {
        background-color: #222222 !important;
    }
    .popper__arrow {
        border-bottom-color: #444444 !important;
        &::after {
            border-bottom-color: #444444 !important;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/page/Consumption/index.vue